import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiDownload, FiUpload, FiFileText, FiX } from 'react-icons/fi';
import { Input, Button, Pagination, Select, Checkbox, message, Modal, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { apiUrl, apiFetch } from '../api';
import PhoneInputPkg from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { getCompanyLabels, getEmployeeTranslatedLabel } from '../utils/companyRoles';

const PhoneInput = PhoneInputPkg.default || PhoneInputPkg;

const FIELD_TYPES = {
  textbox: 'textbox',
  dropdown: 'dropdown',
  checklist: 'checklist',
  date: 'date',
  datetime: 'datetime'
};

const buildDefaultValue = (field) => (field.type === FIELD_TYPES.checklist ? [] : '');

const normalizeOptions = (options) =>
  (Array.isArray(options) ? options : []).map((option) => ({
    value: option,
    label: option
  }));

const createFallbackSection = (title = 'Additional Details') => ({
  id: 'additional-details',
  title,
});

const SYSTEM_FIELD_KEYS = new Set(['firstName', 'lastName', 'email', 'department', 'phoneNumber', 'address']);

import FilterModal from '../components/FilterModal';
import EmployeeDetailsModal from '../components/EmployeeDetailsModal';

const createInitialFormData = () => ({
  firstName: '',
  lastName: '',
  email: '',
  department: '',
  employeeId: '',
  phoneNumber: '',
  address: '',
  status: 'active',
  role: 'employee'
});

const Employees = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{"role": "admin"}');
  const labels = getCompanyLabels(user?.companyName);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilters, setActiveFilters] = useState({});
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalDocs, setTotalDocs] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [createdEmployeeId, setCreatedEmployeeId] = useState('');
  const [formSections, setFormSections] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [formData, setFormData] = useState(createInitialFormData);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);
  const [subDepartmentsList, setSubDepartmentsList] = useState([]);
  const [employeeTypesList, setEmployeeTypesList] = useState([]);
  const [entityLabel, setEntityLabel] = useState(() => labels.employee);
  const [entityLabelPlural, setEntityLabelPlural] = useState(() => labels.employeePlural);
  const fileInputRef = useRef(null);
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const clearFieldError = (name) => {
    setErrors((currentErrors) => {
      if (!currentErrors[name]) return currentErrors;

      const updatedErrors = { ...currentErrors };
      delete updatedErrors[name];
      return updatedErrors;
    });
  };

  const setFieldError = (name, message) => {
    setErrors((currentErrors) => ({
      ...currentErrors,
      [name]: message
    }));
  };

  const fetchEmployees = useCallback(async () => {
    try {
      const searchParam = searchTerm.trim();
      let queryStr = `?page=${page}&limit=${limit}`;
      if (searchParam) queryStr += `&search=${encodeURIComponent(searchParam)}`;
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) queryStr += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      });

      const response = await apiFetch(`/api/employees${queryStr}`);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setEmployees(data.data);
        setTotalDocs(data.totalDocs || 0);
      } else {
        setEmployees([]);
        setTotalDocs(0);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
      setLoading(false);
    }
  }, [page, limit, searchTerm, activeFilters]);

  useEffect(() => {
    const fetchFormConfig = async () => {
      try {
        const response = await apiFetch('/api/employee-fields');
        const data = await response.json();
        if (!response.ok) return;

        const sections = Array.isArray(data.data?.sections)
          ? data.data.sections
            .filter((section) => section.title)
            .sort((left, right) => (left.order || 0) - (right.order || 0))
          : [];
        const fields = Array.isArray(data.data?.fields)
          ? data.data.fields
            .filter((field) => field.enabled)
            .sort((left, right) => (left.order || 0) - (right.order || 0))
          : [];

        if (sections.length > 0) {
          setFormSections(sections);
          setFormFields(fields.map((field) => ({
            ...field,
            sectionId: field.sectionId || sections[0].id,
          })));
        } else if (fields.length > 0) {
          setFormSections([createFallbackSection()]);
          setFormFields(fields.map((field) => ({
            ...field,
            sectionId: field.sectionId || 'additional-details',
          })));
        } else {
          setFormSections([]);
          setFormFields([]);
        }
      } catch (error) {
        console.error('Error fetching employee form fields:', error);
        setFormSections([]);
        setFormFields([]);
      }
    };

    fetchFormConfig();
  }, []);

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const [deptRes, branchRes, subDeptRes, typeRes] = await Promise.all([
          apiFetch('/api/departments?all=true'),
          apiFetch('/api/branches?all=true'),
          apiFetch('/api/sub-departments?all=true'),
          apiFetch('/api/employee-types?all=true')
        ]);

        const deptPayload = await deptRes.json();
        if (deptRes.ok && deptPayload.success && Array.isArray(deptPayload.data)) {
          setDepartmentsList(deptPayload.data);
        }

        const branchPayload = await branchRes.json();
        if (branchRes.ok && branchPayload.success && Array.isArray(branchPayload.data)) {
          setBranchesList(branchPayload.data);
        }

        const subDeptPayload = await subDeptRes.json();
        if (subDeptPayload.success && Array.isArray(subDeptPayload.data)) {
          setSubDepartmentsList(subDeptPayload.data);
        }

        const typePayload = await typeRes.json();
        if (typeRes.ok && typePayload.success && Array.isArray(typePayload.data)) {
          setEmployeeTypesList(typePayload.data);

          const fetchedRoles = typePayload.data.map(r => ({ value: r.name?.toLowerCase(), label: r.name }));

          const defaults = [
            { value: 'admin', label: 'Admin' },
            { value: 'receptionist', label: 'Receptionist' },
            { value: 'employee', label: 'Employee' }
          ];

          const mergedRoles = [...defaults];
          fetchedRoles.forEach(fr => {
            if (!mergedRoles.some(mr => mr.value === fr.value)) {
              mergedRoles.push(fr);
            }
          });

          setRolesList(mergedRoles);
        }
      } catch (error) {
        console.error('Error fetching dropdown settings data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const fetchSiteConfig = async () => {
      try {
        const response = await apiFetch('/api/site-config');
        const data = await response.json();
        if (response.ok && data.data) {
          if (data.data.entityLabel) setEntityLabel(data.data.entityLabel);
          if (data.data.entityLabelPlural) setEntityLabelPlural(data.data.entityLabelPlural);
        }
      } catch (err) {
        // fallback to defaults
      }
    };
    fetchSiteConfig();
  }, []);

  useEffect(() => {
    setCustomFieldValues((currentValues) => {
      const nextValues = {};

      formFields
        .filter((field) => !field.system && !SYSTEM_FIELD_KEYS.has(field.key))
        .forEach((field) => {
          nextValues[field.key] = Object.prototype.hasOwnProperty.call(currentValues, field.key)
            ? currentValues[field.key]
            : buildDefaultValue(field);
        });

      return nextValues;
    });
  }, [formFields]);

  const getSectionFields = (sectionId) => formFields.filter((field) => (field.sectionId || 'additional-details') === sectionId);
  const getSectionIdentifier = (section) => section.id || section._id || 'additional-details';
  const isFieldInRenderedSection = (field) => {
    const targetSectionId = field.sectionId || 'additional-details';
    return formSections.some((section) => getSectionIdentifier(section) === targetSectionId);
  };
  const getRenderableFields = () => formFields.filter((field) => field.enabled !== false && isFieldInRenderedSection(field));

  const getFieldValue = (field) => {
    if (field.system || SYSTEM_FIELD_KEYS.has(field.key)) {
      return formData[field.key] ?? '';
    }
    return customFieldValues[field.key] ?? buildDefaultValue(field);
  };

  const setFieldValue = (field, value) => {
    if (field.system || SYSTEM_FIELD_KEYS.has(field.key)) {
      setFormData((currentData) => ({ ...currentData, [field.key]: value }));
    } else {
      setCustomFieldValues((currentValues) => ({
        ...currentValues,
        [field.key]: value,
      }));
    }

    if (Array.isArray(value) ? value.length > 0 : String(value || '').trim()) {
      clearFieldError(field.key);
    }

    if (field.key === 'email' && String(value || '').trim() && !isValidEmail(value)) {
      setFieldError('email', 'Please enter a valid Email');
    } else if (field.key === 'email' && String(value || '').trim()) {
      clearFieldError('email');
    }
  };

  const renderField = (field) => {
    const fieldError = errors[field.key];
    const fieldValue = getFieldValue(field);
    const translatedLabelText = getEmployeeTranslatedLabel(field.label, user?.companyName);
    const label = (
      <label className="text-[10px] font-medium text-slate-500 dark:text-slate-300 uppercase">
        {translatedLabelText}
        {field.required && <span className="text-rose-500 ml-1">*</span>}
      </label>
    );

    const normalizedKey = (field.key || '').toLowerCase();

    if (normalizedKey === 'department') {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <Select
            className={`w-full h-10 ${fieldError ? 'border-red-500' : ''}`}
            placeholder={field.placeholder || `Select ${translatedLabelText}`}
            value={fieldValue || undefined}
            onChange={(value) => setFieldValue(field, value)}
            options={departmentsList.map(dept => ({ value: dept.name, label: dept.name }))}
            showSearch
            optionFilterProp="label"
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (normalizedKey === 'branch' || normalizedKey.includes('branch')) {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <Select
            className={`w-full h-10 ${fieldError ? 'border-red-500' : ''}`}
            placeholder={field.placeholder || `Select ${translatedLabelText}`}
            value={fieldValue || undefined}
            onChange={(value) => setFieldValue(field, value)}
            options={branchesList.map(b => ({ value: b.name, label: b.name }))}
            showSearch
            optionFilterProp="label"
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (normalizedKey === 'subdepartment' || normalizedKey === 'sub-department' || normalizedKey.includes('subdep') || normalizedKey.includes('sub-dep')) {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <Select
            className={`w-full h-10 ${fieldError ? 'border-red-500' : ''}`}
            placeholder={field.placeholder || `Select ${translatedLabelText}`}
            value={fieldValue || undefined}
            onChange={(value) => setFieldValue(field, value)}
            options={subDepartmentsList.map(sd => ({ value: sd.name, label: sd.name }))}
            showSearch
            optionFilterProp="label"
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (normalizedKey === 'employeetype' || normalizedKey === 'employee-type' || normalizedKey === 'employmenttype' || normalizedKey === 'employment-type' || normalizedKey.includes('emptype') || normalizedKey.includes('emp-type')) {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <Select
            className={`w-full h-10 ${fieldError ? 'border-red-500' : ''}`}
            placeholder={field.placeholder || `Select ${translatedLabelText}`}
            value={fieldValue || undefined}
            onChange={(value) => setFieldValue(field, value)}
            options={employeeTypesList.map(t => ({ value: t.name, label: t.name }))}
            showSearch
            optionFilterProp="label"
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.type === FIELD_TYPES.dropdown) {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <Select
            className={`w-full h-10 ${fieldError ? 'border-red-500' : ''}`}
            placeholder={field.placeholder || `Select ${translatedLabelText}`}
            value={fieldValue || undefined}
            onChange={(value) => setFieldValue(field, value)}
            options={normalizeOptions(field.options)}
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.type === FIELD_TYPES.checklist) {
      return (
        <div key={field.key} className="space-y-1 md:col-span-2">
          {label}
          <Checkbox.Group
            className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3"
            options={normalizeOptions(field.options)}
            value={fieldValue || []}
            onChange={(values) => setFieldValue(field, values)}
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.key === 'phoneNumber') {
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <PhoneInput
            country={'in'}
            value={fieldValue}
            onChange={(phone) => setFieldValue(field, '+' + phone)}
            inputClass={`!w-full !h-10 !rounded-lg !border-slate-200 dark:!bg-slate-800 dark:!border-slate-700 dark:!text-white ${fieldError ? '!border-red-500' : ''}`}
            buttonClass="dark:!bg-slate-800 dark:!border-slate-700"
            dropdownClass="dark:!bg-slate-800 dark:!text-white"
            placeholder={field.placeholder || `Enter ${translatedLabelText}`}
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    if (field.type === FIELD_TYPES.date || field.type === FIELD_TYPES.datetime) {
      const isDateTime = field.type === FIELD_TYPES.datetime;
      return (
        <div key={field.key} className="space-y-1">
          {label}
          <DatePicker
            className={`w-full h-10 ${fieldError ? '[&_.ant-picker]:border-red-500' : ''}`}
            format={isDateTime ? "DD-MM-YYYY HH:mm" : "DD-MM-YYYY"}
            showTime={isDateTime ? { defaultValue: dayjs(), format: 'HH:mm', minuteStep: 5 } : false}
            placeholder={field.placeholder || (isDateTime ? 'Select date and time' : 'Select date')}
            value={fieldValue ? dayjs(fieldValue) : null}
            onChange={(date) => setFieldValue(field, date ? date.toISOString() : '')}
          />
          {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
        </div>
      );
    }

    return (
      <div key={field.key} className={field.key === 'address' ? 'space-y-1 md:col-span-2' : 'space-y-1'}>
        {label}
        <Input
          type={field.key === 'email' ? 'email' : 'text'}
          value={fieldValue}
          onChange={(event) => setFieldValue(field, event.target.value)}
          size="middle"
          className={`rounded-lg h-10 dark:bg-slate-800 dark:border-slate-700 dark:text-white ${fieldError ? 'border-red-500' : ''}`}
          placeholder={field.placeholder || `Enter ${translatedLabelText}`}
        />
        {fieldError && <div className="text-[11px] text-red-500">{fieldError}</div>}
      </div>
    );
  };

  const validateFields = () => {
    const newErrors = {};

    getRenderableFields().forEach((field) => {
      const value = getFieldValue(field);
      const hasTextValue = typeof value === 'string' && value.trim().length > 0;
      const hasChecklistValue = Array.isArray(value) && value.length > 0;
      const hasValue = hasTextValue || hasChecklistValue || (value !== null && value !== undefined && value !== '');

      if (field.required && !hasValue) {
        newErrors[field.key] = `${field.label} is required`;
      }

      if (field.key === 'email' && hasTextValue && !isValidEmail(value)) {
        newErrors.email = 'Please enter a valid Email';
      }

      if (field.key === 'phoneNumber' && hasTextValue && value.length < 10) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      }
    });

    return newErrors;
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchEmployees();
  }, [fetchEmployees]);

  const downloadCsvFile = async (endpoint, filename) => {
    try {
      const response = await apiFetch(endpoint);
      if (!response.ok) {
        const errorData = await response.json();
        message.error(errorData.message || `Failed to download ${filename}`);
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(`Error downloading ${filename}:`, error);
      message.error('Connection error while downloading file.');
    }
  };

  const handleExport = () => {
    downloadCsvFile('/api/employees/export', 'employees_export.csv');
  };

  const handleDownloadSample = () => {
    downloadCsvFile('/api/employees/sample-csv', 'employees_sample.csv');
  };

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiFetch('/api/employees/import', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        if (data.errors && data.errors.length > 0) {
          message.warning({
            content: `${data.message} ${data.errors.join(' | ')}`,
            duration: 8
          });
        } else {
          message.success(data.message || data.msg || 'Import completed');
        }
        fetchEmployees();
      } else {
        message.error(data.message || data.msg || 'Import failed');
        if (data.errors && data.errors.length > 0) {
          data.errors.slice(0, 3).forEach(err => message.warning(err));
        }
      }
    } catch (error) {
      console.error('Error importing:', error);
      message.error('Error importing file');
    }
    // Clear input
    e.target.value = '';
  };

  const handleEdit = (emp) => {
    setEditingId(emp._id);
    setCreatedEmployeeId('');
    setFormData({
      firstName: emp.firstName || '',
      lastName: emp.lastName || '',
      email: emp.email || '',
      department: emp.department || '',
      employeeId: emp.employeeId || '',
      phoneNumber: emp.phoneNumber || '',
      address: emp.address || '',
      status: emp.status || 'active'
    });
    const existingCustomFields = emp.customFields && typeof emp.customFields === 'object' ? emp.customFields : {};
    setCustomFieldValues(
      formFields
        .filter((field) => !field.system && !SYSTEM_FIELD_KEYS.has(field.key))
        .reduce((accumulator, field) => {
          accumulator[field.key] = existingCustomFields[field.key] ?? buildDefaultValue(field);
          return accumulator;
        }, {})
    );
    setErrors({});
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this employee?',
      content: 'This action cannot be undone and will remove the employee from the directory.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await apiFetch(`/api/employees/${id}`, {
            method: 'DELETE'
          });
          if (response.ok) {
            message.success('Employee deleted successfully.');
            fetchEmployees();
          } else {
            message.error('Failed to delete employee.');
          }
        } catch (error) {
          console.error('Error deleting employee:', error);
          message.error('Error occurred while deleting employee.');
        }
      }
    });
  };

  const handleRoleChange = async (value) => {
    setFormData((current) => ({ ...current, role: value }));
    if (!editingId) {
      try {
        const resp = await apiFetch(`/api/employees/next-id?role=${value}`);
        const payload = await resp.json().catch(() => ({}));
        const nextId = resp.ok ? (payload.data?.nextEmployeeId || '') : '';
        setFormData((current) => ({ ...current, employeeId: nextId }));
      } catch (e) {
        console.error('Error fetching next employee id', e);
      }
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const newErrors = validateFields();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      message.error('Please fill in all required fields correctly.');
      const formEl = document.querySelector('.custom-scrollbar');
      if (formEl) {
        formEl.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      department: formData.department,
      phoneNumber: formData.phoneNumber,
      address: formData.address,
      status: formData.status || 'active',
      customFields: customFieldValues,
    };

    // Include role only when adding a new employee
    if (!editingId) {
      payload.role = formData.role || 'employee';
    }

    setErrors({});
    setIsSubmitting(true);
    const path = editingId ? `/api/employees/${editingId}` : '/api/employees';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const response = await apiFetch(path, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const nextEmployeeId = data.data?.employeeId || data.data?.employee?.employeeId || '';
        if (!editingId && nextEmployeeId) {
          setCreatedEmployeeId(nextEmployeeId);
        }
        setShowAddModal(false);
        setEditingId(null);
        setFormData(createInitialFormData());
        setCustomFieldValues(
          formFields
            .filter((field) => !field.system && !SYSTEM_FIELD_KEYS.has(field.key))
            .reduce((accumulator, field) => {
              accumulator[field.key] = buildDefaultValue(field);
              return accumulator;
            }, {})
        );
        setErrors({});
        fetchEmployees();
      } else {
        // Attempt to parse JSON error, otherwise read text
        let errorText = 'Failed to process request';
        try {
          const errJson = await response.json();
          errorText = errJson.message || JSON.stringify(errJson);
        } catch (e) {
          try { errorText = await response.text(); } catch (e2) { /* ignore */ }
        }
        console.error('Employee save failed:', response.status, errorText);
        message.error(errorText);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Error processing request');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Server returns filtered/paginated results when `searchTerm` is present
  const filteredEmployees = employees || [];

  const resetForm = () => {
    setEditingId(null);
    setCreatedEmployeeId('');
    setFormData(createInitialFormData());
    setCustomFieldValues(
      formFields
        .filter((field) => !field.system && !SYSTEM_FIELD_KEYS.has(field.key))
        .reduce((accumulator, field) => {
          accumulator[field.key] = buildDefaultValue(field);
          return accumulator;
        }, {})
    );
    setErrors({});
    setShowAddModal(false);
  };

  const filterConfig = useMemo(() => {
    const baseFilters = [
      {
        key: 'role',
        label: 'Function/Role',
        type: 'select',
        options: rolesList.map(r => ({ label: r.label, value: r.value }))
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: 'active' },
          { label: 'Inactive', value: 'inactive' }
        ]
      }
    ];

    const columnFieldKeys = new Set(['firstName', 'lastName', 'email', 'phoneNumber', 'role', 'status', 'employeeId', 'address']);
    const dynamicFilters = formFields
      .filter(f => !columnFieldKeys.has(f.key) && f.enabled !== false)
      .map(f => {
        if (f.type === 'date' || f.type === 'datetime') {
          return {
            key: f.key,
            label: f.label,
            type: 'date'
          };
        } else {
          let uniqueValues = new Set();
          
          const normalizedKey = (f.key || '').toLowerCase();
          
          if (normalizedKey === 'department') {
            departmentsList.forEach(dept => uniqueValues.add(dept.name));
          } else if (normalizedKey === 'branch' || normalizedKey.includes('branch')) {
            branchesList.forEach(b => uniqueValues.add(b.name));
          } else if (normalizedKey === 'subdepartment' || normalizedKey === 'sub-department' || normalizedKey.includes('subdep') || normalizedKey.includes('sub-dep')) {
            subDepartmentsList.forEach(sd => uniqueValues.add(sd.name));
          } else if (normalizedKey === 'employeetype' || normalizedKey === 'employee-type' || normalizedKey === 'employmenttype' || normalizedKey === 'employment-type' || normalizedKey.includes('emptype') || normalizedKey.includes('emp-type')) {
            employeeTypesList.forEach(t => uniqueValues.add(t.name));
          } else if (Array.isArray(f.options) && f.options.length > 0) {
            f.options.forEach(opt => uniqueValues.add(opt));
          } else {
            employees.forEach(emp => {
              let val = emp[f.key];
              if (!val && emp.customFields) {
                // customFields can be an object or a Map, but over JSON it's an object
                val = emp.customFields[f.key];
              }
              if (val) {
                if (Array.isArray(val)) val.forEach(v => uniqueValues.add(v));
                else uniqueValues.add(val);
              }
            });
          }

          return {
            key: f.key,
            label: f.label,
            type: 'select',
            showSearch: true,
            options: Array.from(uniqueValues).filter(Boolean).map(opt => ({ label: String(opt), value: String(opt) }))
          };
        }
      });

    return [...dynamicFilters, ...baseFilters];
  }, [formFields, rolesList, employees, departmentsList, branchesList, subDepartmentsList, employeeTypesList]);

  const handleApplyFilters = (filters) => {
    setActiveFilters(filters);
    setPage(1);
    setShowFilterModal(false);
  };

  const handleResetFilters = () => {
    setActiveFilters({});
    setPage(1);
    setShowFilterModal(false);
  };

  if (user.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center p-8">
        <div className="w-20 h-20 bg-rose-50 dark:bg-rose-900/20 text-rose-500 rounded-full flex items-center justify-center mb-6">
          <FiX className="w-10 h-10" />
        </div>
        <h2 className="text-3xl font-semibold text-slate-900 dark:text-white mb-2 tracking-tight">Access Denied</h2>
        <p className="text-slate-500 max-w-sm">You do not have permission to view the {entityLabelPlural} directory. Please contact your administrator.</p>
      </div>
    );
  }

  return (
    <div className="wm-page">
      <div className="wm-page-header">
        <div>
          <h1 className="wm-page-title dark:text-white">{entityLabelPlural}</h1>
          <p className="wm-page-subtitle dark:text-slate-400">Manage company {entityLabelPlural.toLowerCase()} and their details.</p>
        </div>
        <div className="flex flex-wrap gap-3 w-full lg:w-auto">
          <Button
            onClick={handleDownloadSample}
            icon={<FiFileText />}
            size="large"
            className="wm-btn-secondary flex items-center h-10"
          >
            Sample CSV
          </Button>
          <Button
            onClick={handleImportClick}
            icon={<FiUpload />}
            size="large"
            className="wm-btn-secondary flex items-center h-10"
          >
            Import CSV
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".csv"
            className="hidden"
          />
          <Button
            onClick={handleExport}
            icon={<FiDownload />}
            size="large"
            className="wm-btn-secondary flex items-center h-10"
          >
            Export Excel
          </Button>
          <Button
            onClick={async () => {
              setErrors({});
              try {
                const resp = await apiFetch('/api/employees/next-id?role=employee');
                const payload = await resp.json().catch(() => ({}));
                const nextId = resp.ok ? (payload.data?.nextEmployeeId || '') : '';
                setFormData((current) => ({ ...current, employeeId: nextId }));
              } catch (e) {
                console.error('Error fetching next employee id', e);
              }
              setShowAddModal(true);
            }}
            icon={<FiPlus />}
            size="large"
            type="primary"
            className="wm-btn-primary flex items-center h-10 px-6"
          >
            Add {entityLabel}
          </Button>
        </div>
      </div>

      {createdEmployeeId && (
        <div className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
          Employee created successfully. EMP Code: <span className="font-semibold">{createdEmployeeId}</span>
        </div>
      )}

      <div className="wm-table-shell dark:bg-slate-900">
        <div className="wm-card-header bg-[var(--wm-surface-muted)] dark:bg-slate-800/20 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-2 max-w-md w-full">
            <Input.Search
              size="large"
              placeholder="Search here"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onSearch={() => setPage(1)}
              className="w-full custom-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button className="h-10 px-5 font-medium bg-[#e6f4ff] text-[#1677ff] border-transparent hover:bg-[#bae0ff]" onClick={() => setShowFilterModal(true)}>Filter</Button>
            <Button 
              className={`h-10 px-5 font-medium ${Object.values(activeFilters).some(v => v) ? 'bg-[#0070b1] text-white hover:bg-[#005a8e] border-transparent' : 'bg-slate-50 border-slate-200 text-slate-500 hover:text-slate-700'}`} 
              onClick={handleResetFilters}
            >
              Reset
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Employee</th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">EMP Code</th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Contact Info</th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Phone</th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Role</th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-[12px] font-medium text-slate-600 dark:text-slate-200 normal-case tracking-normal whitespace-nowrap text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {loading ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx} className="border-b border-slate-200 dark:border-slate-800">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="space-y-2 flex-1 min-w-[120px]">
                          <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-3/4"></div>
                          <div className="h-2.5 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-1/2"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-24"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-32"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-24"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-24"></div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-3 bg-slate-200 dark:bg-slate-800 rounded animate-pulse w-16"></div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end space-x-2">
                        <div className="h-8 w-8 bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
                        <div className="h-8 w-8 bg-slate-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
                      </div>
                    </td>
                  </tr>
                ))
              ) : filteredEmployees.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-8 py-14 text-center text-slate-400 font-medium">
                    No employees found matching your search.
                  </td>
                </tr>
              ) : (
                filteredEmployees.map((emp) => (
                  <tr key={emp._id || emp.id} onClick={() => setSelectedEmployee(emp)} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="min-w-0">
                          <div className="text-[13px] font-normal text-slate-800 dark:text-slate-100 truncate">{emp.firstName || 'Unknown'} {emp.lastName || ''}</div>
                          <div className="text-[11px] text-slate-500 dark:text-slate-400 font-normal truncate leading-tight">{emp.department || 'Staff'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[13px] text-slate-800 dark:text-slate-100 font-normal whitespace-nowrap">{emp.employeeId || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[13px] text-slate-800 dark:text-slate-100 font-normal truncate">{emp.email || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-[13px] text-slate-800 dark:text-slate-100 font-normal whitespace-nowrap">{emp.phoneNumber || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[13px] text-slate-800 dark:text-slate-100 font-normal truncate capitalize">{emp.role || 'employee'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-[11px] font-semibold rounded-full ${emp.status === 'inactive'
                        ? 'bg-rose-50 text-rose-600 dark:bg-rose-950/20 dark:text-rose-400'
                        : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400'
                        }`}>
                        {emp.status === 'inactive' ? 'Inactive' : 'Active'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end space-x-2">
                        <Button onClick={(e) => { e.stopPropagation(); handleEdit(emp); }} type="text" icon={<FiEdit2 className="w-4 h-4" />} className="text-slate-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-xl transition-all border-none" />
                        <Button onClick={(e) => { e.stopPropagation(); handleDelete(emp._id); }} type="text" icon={<FiTrash2 className="w-4 h-4" />} className="text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-all border-none" />
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p-4 flex items-center justify-end">
        <Pagination
          current={page}
          pageSize={limit}
          total={totalDocs}
          showSizeChanger
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={(p, ps) => { setPage(p); setLimit(ps); }}
          onShowSizeChange={(current, size) => { setPage(1); setLimit(size); }}
        />
      </div>

      {/* Add/Edit Employee Modal */}
      <AnimatePresence>
        {showAddModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={resetForm}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-5xl bg-white dark:bg-vms-navy rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] overflow-hidden border border-slate-100 dark:border-white/10"
            >
              {/* Header */}
              <div className="px-8 py-6 border-b border-slate-100 dark:border-white/5 flex justify-between items-center bg-slate-50/50 dark:bg-white/5">
                <h2 className="text-xl font-medium text-slate-800 dark:text-white">{editingId ? `Edit ${entityLabel}` : `Add ${entityLabel}`}</h2>
                <Button
                  onClick={resetForm}
                  icon={<FiX />}
                  type="primary"
                  className="flex items-center gap-2 px-4 py-2 bg-vms-navy dark:bg-vms-accent text-white dark:text-vms-navy rounded-lg text-xs font-medium transition-all hover:opacity-90 active:scale-95 shadow-md border-none h-10"
                >
                  Back
                </Button>
              </div>

              <form onSubmit={handleSubmit} noValidate className="p-8 max-h-[80vh] overflow-y-auto custom-scrollbar">
                <div className="space-y-10">
                  {formSections.length > 0 ? formSections.map((section) => {
                    const sectionFields = getSectionFields(section.id || section._id || 'additional-details');
                    if (sectionFields.length === 0) return null;

                    return (
                      <div key={section.id || section._id || section.title} className="space-y-6">
                        <div className="flex items-center gap-4">
                          <h3 className="text-[10px] font-medium text-slate-400 uppercase tracking-[0.2em] whitespace-nowrap">{section.title}</h3>
                          <div className="h-px bg-slate-100 dark:bg-white/5 w-full"></div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {sectionFields.map((field) => renderField(field))}
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="text-sm text-slate-500 dark:text-slate-400">Loading employee form configuration...</div>
                  )}

                  <div className="space-y-6">
                    <div className="flex items-center gap-4">
                      <h3 className="text-[10px] font-medium text-slate-400 uppercase tracking-[0.2em] whitespace-nowrap">System Reference</h3>
                      <div className="h-px bg-slate-100 dark:bg-white/5 w-full"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <label className="text-[10px] font-medium text-slate-500 dark:text-slate-300 uppercase">Employee ID</label>
                        <Input
                          value={formData.employeeId}
                          readOnly
                          placeholder={editingId ? '' : 'Auto-generated on save'}
                          size="middle"
                          className="rounded-lg h-10 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        />
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">
                          {editingId ? 'Employee ID is maintained automatically for consistency.' : 'Assigned automatically when the employee is created.'}
                        </div>
                      </div>

                      {editingId && (
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-slate-500 dark:text-slate-300 uppercase">Status</label>
                          <Select
                            className="w-full h-10"
                            value={formData.status || 'active'}
                            onChange={(value) => setFormData((current) => ({ ...current, status: value }))}
                            options={[
                              { value: 'active', label: 'Active' },
                              { value: 'inactive', label: 'Inactive' }
                            ]}
                          />
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            Set the current status of the {entityLabel.toLowerCase()}.
                          </div>
                        </div>
                      )}

                      {!editingId && (
                        <div className="space-y-1">
                          <label className="text-[10px] font-medium text-slate-500 dark:text-slate-300 uppercase">Role <span className="text-rose-500">*</span></label>
                          <Select
                            className="w-full h-10"
                            value={formData.role || 'employee'}
                            onChange={handleRoleChange}
                            options={rolesList}
                          />
                          <div className="text-[11px] text-slate-500 dark:text-slate-400">
                            Determines login access level and default password.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-12 pt-8 border-t border-slate-100 dark:border-white/5 flex justify-end">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    size="large"
                    className="px-10 h-14 bg-vms-navy dark:bg-vms-accent text-white dark:text-vms-navy rounded-xl font-medium uppercase tracking-widest text-xs shadow-xl transition-all border-none"
                  >
                    {editingId ? `Update ${entityLabel}` : `Save ${entityLabel} Details`}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <FilterModal
        open={showFilterModal}
        onCancel={() => setShowFilterModal(false)}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        config={filterConfig}
        initialValues={activeFilters}
      />

      <EmployeeDetailsModal
        open={!!selectedEmployee}
        onCancel={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
      />
    </div>
  );
};

export default Employees;
